import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusPill } from "@/components/StatusPill";
import { ChevronLeft, MapPin, Calendar as CalendarIcon, User, Briefcase, Target } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function CampaignDetail() {
  const { campaign_id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [timeline, setTimeline] = useState({ executions: [], plannings: [] });

  useEffect(() => {
    api.get(`/campaigns/${campaign_id}`).then((r) => setCampaign(r.data));
    api.get(`/campaigns/${campaign_id}/timeline`).then((r) => setTimeline(r.data));
  }, [campaign_id]);

  if (!campaign) return <div className="text-sm text-zinc-500">Loading campaign…</div>;
  const pct = campaign.target_installations ? Math.round((campaign.completed_installations / campaign.target_installations) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="campaign-detail-page">
      <Link to="/campaigns" className="inline-flex items-center text-xs text-zinc-600 hover:text-zinc-950 font-medium" data-testid="back-to-campaigns">
        <ChevronLeft className="h-4 w-4" /> Back to campaigns
      </Link>

      {/* Hero */}
      <Card className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-zinc-500" data-testid="detail-campaign-id">{campaign.campaign_id}</span>
              <StatusPill status={campaign.health} testId="detail-health-pill" />
              <StatusPill status={campaign.campaign_status} testId="detail-status-pill" />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="detail-campaign-name">{campaign.campaign_name}</h1>
            <p className="text-sm text-zinc-600 mt-1">{campaign.client_name} · {campaign.media_type}</p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <Stat icon={MapPin} label="City" value={campaign.city} />
            <Stat icon={Briefcase} label="Pod" value={campaign.pod || "—"} />
            <Stat icon={User} label="ADO" value={campaign.ado_name || "—"} />
            <Stat icon={Target} label="Target" value={campaign.target_installations} />
          </div>
        </div>
        {/* Progress */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-zinc-600 mb-1.5">
            <span className="uppercase tracking-wider font-semibold">Progress</span>
            <span className="font-mono">{campaign.completed_installations} / {campaign.target_installations} ({pct}%)</span>
          </div>
          <div className="h-2 w-full bg-zinc-100 rounded-full overflow-hidden" data-testid="detail-progress-bar">
            <div className={`h-full transition-all ${campaign.health === "red" ? "bg-red-500" : campaign.health === "amber" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs text-zinc-500 mt-2 font-mono">
            <span><CalendarIcon className="h-3 w-3 inline mr-1" />Start: {campaign.start_date}</span>
            <span>End: {campaign.end_date}</span>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="executions">
        <TabsList data-testid="detail-tabs">
          <TabsTrigger value="executions" data-testid="tab-executions">Execution Updates</TabsTrigger>
          <TabsTrigger value="planning" data-testid="tab-planning">Planning</TabsTrigger>
          <TabsTrigger value="trend" data-testid="tab-trend">Trend</TabsTrigger>
        </TabsList>

        <TabsContent value="executions" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
                <tr>
                  <th className="text-left py-2.5 px-4 font-semibold">Date</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Planned</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Completed T</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Completed N.T</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Total</th>
                  <th className="text-left py-2.5 px-4 font-semibold">% Achieved</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Onsite</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Risk</th>
                  <th className="text-left py-2.5 px-4 font-semibold">By</th>
                </tr>
              </thead>
              <tbody>
                {timeline.executions.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-zinc-500 text-xs">No execution updates yet.</td></tr>
                )}
                {timeline.executions.map((e) => {
                  const tVal = e.installations_completed_t ?? e.installations_completed ?? 0;
                  const ntVal = e.installations_completed_nt ?? 0;
                  const total = (e.installations_completed != null) ? e.installations_completed : (tVal + ntVal);
                  return (
                    <tr key={e.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`execution-row-${e.id}`}>
                      <td className="py-2.5 px-4 font-mono text-xs">{e.update_date}</td>
                      <td className="py-2.5 px-4">{e.installations_planned}</td>
                      <td className="py-2.5 px-4 font-semibold">{tVal}</td>
                      <td className="py-2.5 px-4">{ntVal}</td>
                      <td className="py-2.5 px-4 font-mono">{total}</td>
                      <td className="py-2.5 px-4 font-mono text-xs">{e.daily_target_vs_actual_percent}%</td>
                      <td className="py-2.5 px-4">{e.pending_onsite_count}</td>
                      <td className="py-2.5 px-4">
                        {e.predictive_risk_flag ? <StatusPill status="red">Risk</StatusPill> : <StatusPill status="green">OK</StatusPill>}
                      </td>
                      <td className="py-2.5 px-4 text-xs text-zinc-500">{e.updated_by}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="planning" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
                <tr>
                  <th className="text-left py-2.5 px-4 font-semibold">Planned Date</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Daily Target</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Week</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Team</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Installer</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Slot</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Cluster</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Resource</th>
                </tr>
              </thead>
              <tbody>
                {timeline.plannings.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-zinc-500 text-xs">No planning entries yet.</td></tr>
                )}
                {timeline.plannings.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`planning-row-${p.id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs">{p.planned_date}</td>
                    <td className="py-2.5 px-4 font-mono font-semibold">{p.daily_target ?? 0}</td>
                    <td className="py-2.5 px-4">W{p.planned_week}</td>
                    <td className="py-2.5 px-4">{p.assigned_team}</td>
                    <td className="py-2.5 px-4">{p.assigned_installer}</td>
                    <td className="py-2.5 px-4 capitalize">{p.capacity_slot?.replace("_", " ")}</td>
                    <td className="py-2.5 px-4 text-zinc-600">{p.route_or_cluster}</td>
                    <td className="py-2.5 px-4 capitalize">{p.resource_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="trend" className="mt-4">
          <Card className="p-5">
            <h3 className="font-display text-lg font-semibold mb-2">Daily Achievement Trend</h3>
            <p className="text-xs text-zinc-500 mb-4">Planned vs completed installations</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeline.executions.map((e) => ({ date: e.update_date, planned: e.installations_planned, completed: e.installations_completed }))}>
                  <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Area type="monotone" dataKey="planned" stroke="#a1a1aa" fill="#a1a1aa" fillOpacity={0.2} />
                  <Area type="monotone" dataKey="completed" stroke="#0055FF" fill="#0055FF" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-md">
      <Icon className="h-4 w-4 text-zinc-500" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
